import {
  AbandonedOutdoorActivityResultType,
  Activity,
  AutoClearCooldownFromFeedOutdoorActivityType,
  AutoClearCooldownFromInvestigateOutdoorActivityType,
  BouncedOutdoorActivityResultType,
  DroppedOutdoorActivityResultType,
  EngagementPromotedToInvestigateOutdoorActivityResultType,
  EngagementQuickClearedOutdoorActivityResultType,
  EscalateToIntrusionWithDescriptionOutdoorActivityType,
  FaceDetectionType,
  FamiliarFaceReviewWithoutAiOutdoorActivity,
  FamiliarFaceReviewWithoutAIOutdoorActivityType,
  FamiliarFacesIdentifiedOutdoorActivityResultType,
  FinalDocumentationOutdoorActivityType,
  MonitoringSpecialistAssignmentOutdoorActivityType,
  MonitoringSpecialistTriageOutdoorActivityType,
  MotionDetectionType,
  NoFamiliarFacesIdentifiedOutdoorActivityResultType,
  OutdoorEngagement,
  OutdoorEngagementResolutionCategory,
  PersonDetectionType,
  QuickClearFromInvestigateOutdoorActivityType
} from '../../../../api/models'
import { DateTime } from 'luxon'
import { RRWEB_EVENTS, RRWEB_VIDEO_PLACEHOLDER_NAMES } from '@simplisafe/compass-web'
import { ProcessedRRWebEvents, VideoMirrorStartEvent, VideoStartEvent } from '../rrweb/types'
import { CROSS_NAV_PLAYER_PREFIX, STACKED_PLAYER_PREFIX } from '../constants'

export enum TimelineFragmentKind {
  Detection = 'Detection',
  Triage = 'Triage',
  FamiliarFace = 'FamiliarFace',
  LiveStreamView = 'LiveStreamView',
  TwoWayAudio = 'TwoWayAudio',
  Cleared = 'Cleared',
  LiveStreamFailed = 'LiveStreamFailed',
  Escalated = 'Escalated',
  ClearedByAI = 'ClearedByAI',
  Bounced = 'Bounced',
  Dropped = 'Dropped',
  Abandoned = 'Abandoned'
}

export interface SubtextLine {
  label?: string
  text: string
}

export interface SectionText {
  header: string
  subtext?: string | SubtextLine[]
  time: string // ISO timestamp
  kind: TimelineFragmentKind
}

export type SectionResult = SectionText | null

export type TimelineEndColor = 'green' | 'red'

export interface RRWebSummary {
  numberOfCamerasViewedLive: number
  hasSuccessfulRecording: boolean
  isLive: boolean
  firstLiveViewTimestamp?: string // ISO timestamp
  firstSuccessfulViewTimestamp?: string // ISO timestamp
}

// Museum's activity timestamps aren't uniformly formatted, so comparisons parse through Luxon rather than
// comparing ISO strings directly or via `new Date(...).getTime()`.
export function toMillis(timestamp: string): number {
  return DateTime.fromISO(timestamp).toMillis()
}

const STANDALONE_CLEARING_ACTIVITY_TYPES: ReadonlySet<Activity['type']> = new Set([
  FinalDocumentationOutdoorActivityType.FinalDocumentation,
  QuickClearFromInvestigateOutdoorActivityType.QuickClearFromInvestigate,
  AutoClearCooldownFromFeedOutdoorActivityType.AutoClearCooldownFromFeed,
  AutoClearCooldownFromInvestigateOutdoorActivityType.AutoClearCooldownFromInvestigate
])

const AUTO_CLEARING_ACTIVITY_TYPES: ReadonlySet<Activity['type']> = new Set([
  AutoClearCooldownFromFeedOutdoorActivityType.AutoClearCooldownFromFeed,
  AutoClearCooldownFromInvestigateOutdoorActivityType.AutoClearCooldownFromInvestigate
])

// A quick clear that happens during Feed triage has no standalone activity type of its own - unlike
// QuickClearFromInvestigate, it shows up as a MonitoringSpecialistTriage activity whose completion
// result is EngagementQuickCleared.
function isQuickClearedFromFeed(activity: Activity): boolean {
  return (
    activity.type === MonitoringSpecialistTriageOutdoorActivityType.MonitoringSpecialistTriage &&
    activity.completed?.result?.type === EngagementQuickClearedOutdoorActivityResultType.EngagementQuickCleared
  )
}

// The API's ordering isn't a documented guarantee, so this picks by timestamp rather than array position.
export function getLastActivity(activities: Activity[]): Activity | undefined {
  return activities.reduce<Activity | undefined>(
    (latest, activity) => (!latest || toMillis(getActivityTimestamp(activity)) > toMillis(getActivityTimestamp(latest)) ? activity : latest),
    undefined
  )
}

const FAILED_ENDING_KINDS: Partial<Record<string, TimelineFragmentKind>> = {
  [BouncedOutdoorActivityResultType.Bounced]: TimelineFragmentKind.Bounced,
  [DroppedOutdoorActivityResultType.Dropped]: TimelineFragmentKind.Dropped,
  [AbandonedOutdoorActivityResultType.Abandoned]: TimelineFragmentKind.Abandoned
}

// A trailing assignment is just an agent being (re)assigned after the engagement already ended, so the activity before
// it is the one that says how it ended. Anything else after an earlier Abandoned result means it was picked back up.
export function getEndingActivity(activities: Activity[]): Activity | undefined {
  const lastActivity = getLastActivity(activities)
  if (lastActivity?.type !== MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment) return lastActivity
  return getLastActivity(activities.filter(activity => activity !== lastActivity)) ?? lastActivity
}

function findFailedEndingActivity(activities: Activity[]): Activity | undefined {
  const endingActivity = getEndingActivity(activities)
  const resultType = endingActivity?.completed?.result?.type
  return resultType && FAILED_ENDING_KINDS[resultType] ? endingActivity : undefined
}

// The customer disarming clears the engagement, which can leave the agent's activity completed as Abandoned. The
// resolution is the engagement's own record of how it ended, so it wins over a failed ending (an escalation still
// takes precedence over both, as in the Outcome).
export function wasClearedByCustomer(engagement: OutdoorEngagement, activities: Activity[]): boolean {
  return engagement.result?.resolution?.category === OutdoorEngagementResolutionCategory.ClearedByCustomer && !hasEscalateActivity(activities)
}

// The activity whose result makes the engagement bounced/dropped/abandoned, if any
export function getFailedEndingActivity(engagement: OutdoorEngagement, activities: Activity[]): Activity | undefined {
  return wasClearedByCustomer(engagement, activities) ? undefined : findFailedEndingActivity(activities)
}

export function getFailedEndingKind(engagement: OutdoorEngagement, activities: Activity[]): TimelineFragmentKind | undefined {
  const resultType = getFailedEndingActivity(engagement, activities)?.completed?.result?.type
  return resultType ? FAILED_ENDING_KINDS[resultType] : undefined
}

// A clearing activity earlier in the timeline doesn't count if something happened after it (e.g. the
// engagement was re-opened and later escalated) - only the last chronological activity will be considered.
export function hasClearingActivity(activities: Activity[]): boolean {
  const lastActivity = getLastActivity(activities)
  if (!lastActivity || findFailedEndingActivity(activities)) return false
  return STANDALONE_CLEARING_ACTIVITY_TYPES.has(lastActivity.type) || isQuickClearedFromFeed(lastActivity)
}

export function getAutoClearedActivity(activities: Activity[]): Activity | undefined {
  const lastActivity = getLastActivity(activities)
  return lastActivity && AUTO_CLEARING_ACTIVITY_TYPES.has(lastActivity.type) ? lastActivity : undefined
}

export function wasAutoCleared(activities: Activity[]): boolean {
  return !!getAutoClearedActivity(activities)
}

// AutoClearCooldownFromInvestigate means a specialist already promoted this - only the Feed variant
// means no specialist ever saw it.
export function wasAutoClearedFromFeed(activities: Activity[]): boolean {
  return getAutoClearedActivity(activities)?.type === AutoClearCooldownFromFeedOutdoorActivityType.AutoClearCooldownFromFeed
}

export function neverReachedSpecialist(engagement: OutdoorEngagement, activities: Activity[]): boolean {
  return wasClearedByAI(engagement) || wasAutoClearedFromFeed(activities)
}

function hasEscalateActivity(activities: Activity[]): boolean {
  return activities.some(activity => activity.type === EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription)
}

// AI clearing this before it ever reached a specialist is recorded as a resolution category
export function wasClearedByAI(engagement: OutdoorEngagement): boolean {
  return engagement.result?.resolution?.category === OutdoorEngagementResolutionCategory.ClearedByAI
}

// The customer disarming clears the engagement with no clearing activity of its own, so the resolution is the
// only record of it. An escalation takes precedence (as in the Outcome), and a clearing activity already
// supplies the timestamp itself.
export function getClearedByCustomerTimestamp(engagement: OutdoorEngagement, activities: Activity[]): string | undefined {
  const { category, timestamp } = engagement.result?.resolution ?? {}
  if (category !== OutdoorEngagementResolutionCategory.ClearedByCustomer || hasEscalateActivity(activities) || hasClearingActivity(activities)) {
    return undefined
  }
  return timestamp
}

export function getActivityTimestamp(activity: Activity): string {
  return activity.completed?.timestamp ?? activity.started?.timestamp ?? activity.created.timestamp
}

// An engagement can carry more than one activity of the same type (e.g. re-triaged after being reopened) -
// picks by timestamp rather than by array position, since the API's ordering isn't a documented guarantee.
export function findActivityOfType<T extends Activity['type']>(activities: Activity[], type: T): Extract<Activity, { type: T }> | undefined {
  return activities
    .filter((activity): activity is Extract<Activity, { type: T }> => activity.type === type)
    .reduce<Extract<Activity, { type: T }> | undefined>(
      (latest, activity) => (!latest || toMillis(getActivityTimestamp(activity)) > toMillis(getActivityTimestamp(latest)) ? activity : latest),
      undefined
    )
}

const FAMILIAR_FACE_DECISION_RESULT_TYPES = new Set<string>([
  FamiliarFacesIdentifiedOutdoorActivityResultType.FamiliarFacesIdentified,
  NoFamiliarFacesIdentifiedOutdoorActivityResultType.NoFamiliarFacesIdentified
])

// The review that decided who was there: the latest one that completed with faces identified or not. A later review can
// end as Superseded/Abandoned/EngagementResolved without a result of its own, and shouldn't hide an earlier decision. With
// no decision at all, the latest review is used so it still shows as reviewed.
export function findFamiliarFaceReview(activities: Activity[]): FamiliarFaceReviewWithoutAiOutdoorActivity | undefined {
  const type = FamiliarFaceReviewWithoutAIOutdoorActivityType.FamiliarFaceReviewWithoutAI
  const decidedReviews = activities.filter(
    activity => activity.type === type && FAMILIAR_FACE_DECISION_RESULT_TYPES.has(activity.completed?.result?.type as string)
  )
  return findActivityOfType(decidedReviews, type) ?? findActivityOfType(activities, type)
}

// Counts faces the agent confirmed as matches in the decided FamiliarFaceReview's completed result
export function countMatchedFamiliarFaces(activity?: FamiliarFaceReviewWithoutAiOutdoorActivity): number {
  const result = activity?.completed?.result
  if (!result || !('familiarFaces' in result)) return 0
  return result.familiarFaces.filter(face => face.isMatch).length
}

// When the agent was assigned - undefined if the engagement never reached one (e.g. cleared by AI). The latest assignment
// only counts if it isn't the very last activity; a trailing one is a re-assignment after the engagement already ended,
// so the assignment before it (if any) is the one the handling started from.
export function getAgentAssignedTimestamp(activities: Activity[]): string | undefined {
  const assignmentType = MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment
  const latestAssignment = findActivityOfType(activities, assignmentType)
  const isTrailing = !!latestAssignment && latestAssignment === getLastActivity(activities)
  const assignment = isTrailing
    ? findActivityOfType(
        activities.filter(activity => activity !== latestAssignment),
        assignmentType
      )
    : undefined
  return (assignment ?? latestAssignment)?.completed?.timestamp
}

// The first detection's own record only carries ptsSeconds (an offset into its recording), so the absolute
// time has to be derived from the recording that produced it
export function findFirstDetection(
  engagement: OutdoorEngagement
): { type: PersonDetectionType | FaceDetectionType | MotionDetectionType; time: string } | undefined {
  for (const recording of engagement.videoRecordings ?? []) {
    const detection = recording.analysis?.detections?.[0]
    if (detection) {
      return {
        type: detection.type,
        time: new Date(new Date(recording.timestamp).getTime() + detection.ptsSeconds * 1000).toISOString()
      }
    }
  }
  return undefined
}

function earliestTimestamp(events: VideoStartEvent[]): string | undefined {
  return events.length > 0 ? new Date(Math.min(...events.map(event => event.startTimeEpoch))).toISOString() : undefined
}

// Whether the event describes a clip with anything to watch, judged from the recording alone. This mirrors the
// "no duration" checks addStreamingUrl makes before fetching playback, without the fetch itself.
function hasWatchableDuration({ isLive, startTimeEpoch, endTimeEpoch, liveStreamHadNoDuration, recordingDetails }: VideoStartEvent): boolean {
  return isLive ? !liveStreamHadNoDuration && startTimeEpoch !== endTimeEpoch : !!recordingDetails?.durationMillis
}

// Folds the raw per-camera video-start events into the aggregate facts buildTimelineFragments needs.
// A camera only counts as "viewed live" once it produced a live video-start event with something to watch.
// Only counts events on Feed's own players (the stacked recorded players and the persistent cross-nav live
// player).
//
// videoAccessError is deliberately not used here, or to report a live-stream failure to the reviewer (see
// getRecordedLiveStreamFailureReason in timelineFragmentsUtils.ts) - it records what happened just now, when
// this page re-fetched the clip from mediator/museum for playback, not what happened during the engagement
// itself. A customer deleting their recording, a QA opt-out, a mediator 429, or the reviewer lacking the
// recording-playback scope all surface there with no bearing on whether the agent watched anything.
export function computeLiveStreamFacts({ videoStartEvents }: ProcessedRRWebEvents, activities: Activity[]): RRWebSummary {
  const triageCompletion = findActivityOfType(activities, MonitoringSpecialistTriageOutdoorActivityType.MonitoringSpecialistTriage)?.completed
  const promotedToInvestigateMillis =
    triageCompletion?.result.type === EngagementPromotedToInvestigateOutdoorActivityResultType.EngagementPromotedToInvestigate
      ? toMillis(triageCompletion.timestamp)
      : undefined

  const feedEvents = videoStartEvents.filter(event => {
    const placeholder = event.videoPlaceholderName ?? ''
    const isFeedPlayer = placeholder.startsWith(STACKED_PLAYER_PREFIX) || placeholder.startsWith(CROSS_NAV_PLAYER_PREFIX)
    return isFeedPlayer && (promotedToInvestigateMillis === undefined || event.eventTime < promotedToInvestigateMillis)
  })

  const successfulEvents = feedEvents.filter(hasWatchableDuration)
  const successfulLiveEvents = successfulEvents.filter(event => event.isLive)
  // hasSuccessfulRecording specifically means a recorded/played-back video was viewed, as distinct from
  // isLive - a successful live event shouldn't also count toward this, or "live and recorded video" gets
  // reported when only live was ever watched.
  const successfulRecordedEvents = successfulEvents.filter(event => !event.isLive)
  const successfulLiveCameraIds = new Set(successfulLiveEvents.map(event => event.cameraId))

  return {
    numberOfCamerasViewedLive: successfulLiveCameraIds.size,
    hasSuccessfulRecording: successfulRecordedEvents.length > 0,
    isLive: successfulLiveEvents.length > 0,
    firstLiveViewTimestamp: earliestTimestamp(successfulLiveEvents),
    firstSuccessfulViewTimestamp: earliestTimestamp(successfulEvents)
  }
}

export interface MainCanvasLiveStreamFacts {
  numberOfCamerasViewedLive: number
  firstLiveViewTimestamp?: string // ISO timestamp
  firstLiveFailureTimestamp?: string // ISO timestamp
}

function liveViewingKey({
  cameraId,
  videoPlaceholderName,
  startTimeEpoch
}: Pick<VideoStartEvent, 'cameraId' | 'videoPlaceholderName' | 'startTimeEpoch'>) {
  return `${cameraId}|${videoPlaceholderName}|${startTimeEpoch}`
}

function earliestEventTimestamp(events: VideoMirrorStartEvent[]): string | undefined {
  return events.length > 0 ? new Date(Math.min(...events.map(event => event.eventTime))).toISOString() : undefined
}

// What the "Agent viewed live streams" and "Live stream failed" rows report: the live streams that actually
// reached the main video canvas, as opposed to computeLiveStreamFacts, which only sees what was on Feed's own
// players. A mirror matches a live viewing by camera, placeholder and start time. It counts as viewed when that
// viewing had something to watch, and as failed when it had nothing to watch (the same "no duration" check).
export function computeMainCanvasLiveStreamFacts({ videoStartEvents, videoMirrorEvents }: ProcessedRRWebEvents): MainCanvasLiveStreamFacts {
  const liveViewings = videoStartEvents.filter(event => event.isLive)
  const successfulLiveViewingKeys = new Set(liveViewings.filter(hasWatchableDuration).map(liveViewingKey))
  const failedLiveViewingKeys = new Set(liveViewings.filter(event => !hasWatchableDuration(event)).map(liveViewingKey))

  const mainCanvasMirrorStarts = videoMirrorEvents.filter(
    (event): event is VideoMirrorStartEvent =>
      event.eventName === RRWEB_EVENTS.VIDEO_MIRROR_START && event.videoMirrorPlaceholderName === RRWEB_VIDEO_PLACEHOLDER_NAMES.MAIN_VIDEO_CANVAS
  )
  const viewedMirrorStarts = mainCanvasMirrorStarts.filter(event => successfulLiveViewingKeys.has(liveViewingKey(event)))
  const failedMirrorStarts = mainCanvasMirrorStarts.filter(event => failedLiveViewingKeys.has(liveViewingKey(event)))

  return {
    numberOfCamerasViewedLive: new Set(viewedMirrorStarts.map(event => event.cameraId)).size,
    firstLiveViewTimestamp: earliestEventTimestamp(viewedMirrorStarts),
    firstLiveFailureTimestamp: earliestEventTimestamp(failedMirrorStarts)
  }
}

// The rrweb pipeline's timestamps are client-side and can drift from the server-recorded activity
// timestamps that bound the engagement - predating triage, or (e.g. a specialist reviewing footage after
// already clearing/escalating) trailing the terminal outcome. Clamp every RRWebSummary timestamp into
// [triage, terminal] when it falls outside that range. orderTimelineFragments then places the row via its
// stable, array-order tiebreak - which is why the terminal fragments are pushed last in buildTimelineFragments.
export function clampToRange(timestamp: string | undefined, minTimestamp: string | undefined, maxTimestamp: string | undefined): string | undefined {
  if (!timestamp) return timestamp

  // A malformed range (min after max - e.g. a stray triage activity logged after an AI auto-clear) must not
  // let the lower bound push the result past max: that would sort this row after the terminal outcome it's
  // meant to precede (see the comment above).
  const effectiveMin = minTimestamp && maxTimestamp && toMillis(minTimestamp) > toMillis(maxTimestamp) ? maxTimestamp : minTimestamp

  if (effectiveMin && toMillis(timestamp) < toMillis(effectiveMin)) return effectiveMin
  if (maxTimestamp && toMillis(timestamp) > toMillis(maxTimestamp)) return maxTimestamp
  return timestamp
}

// The latest of whichever terminal outcome(s) apply - normally only one of Cleared/Escalated/ClearedByAI/failed ending
// fires per engagement, but taking the max keeps this correct even if that ever stops holding (e.g. an
// earlier, now-superseded escalation shouldn't win over a later clear).
export function getTerminalTimestamp(engagement: OutdoorEngagement, activities: Activity[]): string | undefined {
  const lastActivity = getLastActivity(activities)
  const clearedTimestamp = lastActivity && hasClearingActivity(activities) ? getActivityTimestamp(lastActivity) : undefined
  const escalateActivity = findActivityOfType(activities, EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription)
  const escalatedTimestamp = escalateActivity && getActivityTimestamp(escalateActivity)
  const clearedByAITimestamp = wasClearedByAI(engagement) ? engagement.result?.resolution?.timestamp : undefined
  const clearedByCustomerTimestamp = getClearedByCustomerTimestamp(engagement, activities)

  const failedEndingActivity = getFailedEndingActivity(engagement, activities)
  const failedEndingTimestamp = failedEndingActivity && getActivityTimestamp(failedEndingActivity)

  const candidates = [clearedTimestamp, escalatedTimestamp, clearedByAITimestamp, clearedByCustomerTimestamp, failedEndingTimestamp].filter(
    (timestamp): timestamp is string => !!timestamp
  )
  return candidates.length === 0 ? undefined : candidates.reduce((latest, t) => (toMillis(t) > toMillis(latest) ? t : latest))
}

function toMinuteEpoch(time: string): number {
  const date = new Date(time)
  date.setSeconds(0, 0)
  return date.getTime()
}

export function orderTimelineFragments(fragments: SectionText[]): SectionText[] {
  return [...fragments].sort((a, b) => toMinuteEpoch(a.time) - toMinuteEpoch(b.time))
}

export function getTimelineEndColor(engagement: OutdoorEngagement, activities: Activity[]): TimelineEndColor | undefined {
  if (hasEscalateActivity(activities) || getFailedEndingKind(engagement, activities)) return 'red'
  if (hasClearingActivity(activities) || wasClearedByAI(engagement) || getClearedByCustomerTimestamp(engagement, activities)) return 'green'
  return undefined
}
